import {
  CHARTER_SECTION_LABELS,
  CHARTER_SECTION_ORDER,
  CHARTER_SECTION_FIELDS,
  CHARTER_STATUS_LABEL,
  charterScopeTitle,
} from './initiation';

function esc(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function displayValue(value) {
  if (value == null || value === '') return 'Not set';
  return String(value);
}

function para(text) {
  const lines = displayValue(text).split('\n');
  return lines.map((line) => `<p class="field-value">${esc(line) || '&nbsp;'}</p>`).join('');
}

function fieldBlock(label, value) {
  return (
    `<div class="field">`
    + `<p class="field-label">${esc(label)}</p>`
    + para(value)
    + `</div>`
  );
}

function buildStakeholdersHtml(stakeholders) {
  const list = stakeholders || [];
  if (!list.length) {
    return '<p class="field-value muted">No stakeholders added</p>';
  }
  const rows = list.map((s) => (
    `<tr><td>${esc(s.name || '—')}</td><td>${esc(s.role || '—')}</td></tr>`
  )).join('');
  return (
    `<table class="data-table"><thead><tr><th>Name</th><th>Role</th></tr></thead><tbody>${rows}</tbody></table>`
  );
}

function sectionHtml(sectionKey, content) {
  if (sectionKey === 'stakeholders') {
    return buildStakeholdersHtml(content.stakeholders);
  }
  if (sectionKey === 'constraints_assumptions') {
    return (
      fieldBlock('Constraints', (content.constraints || []).map((c) => (typeof c === 'string' ? c : c.text)).join('\n'))
      + fieldBlock('Key assumptions', content.key_assumptions)
    );
  }
  return (CHARTER_SECTION_FIELDS[sectionKey] || [])
    .map((f) => fieldBlock(f.label, content[f.key]))
    .join('');
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatBudget(amount, currency) {
  if (amount == null || amount === '') return null;
  const num = Number(amount);
  if (Number.isNaN(num)) return String(amount);
  return `${currency || ''} ${num.toLocaleString()}`.trim();
}

function slugify(name) {
  return String(name || 'charter')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'charter';
}

/**
 * Build Word-compatible HTML for a programme or project charter.
 */
export function buildCharterWordHtml({
  charter,
  header,
  sections,
  programmeName,
  projectName,
  charterScope,
}) {
  const scopeTitle = charterScopeTitle(charterScope);
  const entityName = charterScope === 'project' ? (projectName || 'Project') : (programmeName || 'Programme');
  const status = CHARTER_STATUS_LABEL[charter?.status] || charter?.status || 'Draft';
  const title = header?.title || charter?.title || scopeTitle;
  const summary = header?.summary ?? charter?.summary;
  const budget = formatBudget(
    header?.budget_envelope ?? charter?.budget_envelope,
    header?.budget_currency || charter?.budget_currency || 'GBP'
  );

  const metaLines = [
    programmeName ? `Programme: ${programmeName}` : null,
    projectName && charterScope === 'project' ? `Project: ${projectName}` : null,
    `Status: ${status}`,
    charter?.submitted_at ? `Submitted: ${formatDate(charter.submitted_at)}` : null,
    charter?.approved_at ? `Approved: ${formatDate(charter.approved_at)}` : null,
    `Exported: ${formatDate(new Date().toISOString())}`,
  ].filter(Boolean);

  const sectionMap = Object.fromEntries((sections || []).map((s) => [s.section_key, s]));
  const bodySections = CHARTER_SECTION_ORDER.map((key) => {
    const row = sectionMap[key];
    const content = row?.content_json || {};
    const heading = row?.title || CHARTER_SECTION_LABELS[key] || key;
    return (
      `<h2>${esc(heading)}</h2>`
      + sectionHtml(key, content)
    );
  }).join('');

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:w="urn:schemas-microsoft-com:office:word"
  xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<title>${esc(title)}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
<style>
  @page { margin: 2cm; }
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #2c2c2a; line-height: 1.45; }
  h1 { font-size: 22pt; color: #3C3489; margin: 0 0 6pt; font-weight: 600; }
  h2 { font-size: 13pt; color: #534AB7; margin: 18pt 0 8pt; padding-bottom: 4pt; border-bottom: 1pt solid #d3d1c7; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5pt; }
  .subtitle { font-size: 12pt; color: #5f5e5a; margin: 0 0 14pt; }
  .meta { font-size: 10pt; color: #5f5e5a; margin: 0 0 18pt; }
  .meta p { margin: 2pt 0; }
  .summary { background: #fafaf8; border-left: 3pt solid #AFA9EC; padding: 10pt 12pt; margin: 0 0 16pt; }
  .summary p { margin: 0; }
  .field { margin: 0 0 10pt; }
  .field-label { font-size: 9pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4pt; color: #888780; margin: 0 0 3pt; }
  .field-value { margin: 0 0 4pt; }
  .muted { color: #B4B2A9; font-style: italic; }
  .badge-row { margin: 0 0 12pt; }
  .badge { display: inline-block; background: #EEEDFE; color: #3C3489; padding: 3pt 8pt; border-radius: 4pt; font-size: 10pt; }
  table.data-table { width: 100%; border-collapse: collapse; margin: 4pt 0 10pt; }
  table.data-table th, table.data-table td { border: 0.5pt solid #d3d1c7; padding: 6pt 8pt; text-align: left; vertical-align: top; }
  table.data-table th { background: #fafaf8; font-size: 9pt; text-transform: uppercase; color: #888780; }
  ul { margin: 4pt 0 8pt 18pt; padding: 0; }
  li { margin-bottom: 3pt; }
  .footer { margin-top: 24pt; padding-top: 10pt; border-top: 0.5pt solid #d3d1c7; font-size: 9pt; color: #888780; }
</style>
</head>
<body>
  <h1>${esc(title)}</h1>
  <p class="subtitle">${esc(scopeTitle)} · ${esc(entityName)} · IN-01</p>
  <div class="meta">${metaLines.map((line) => `<p>${esc(line)}</p>`).join('')}</div>
  ${budget ? `<div class="badge-row"><span class="badge">Budget envelope: ${esc(budget)}</span></div>` : ''}
  ${summary ? `<div class="summary"><p class="field-label">Summary</p>${para(summary)}</div>` : ''}
  ${bodySections}
  <div class="footer">Generated from Transformax · ${esc(scopeTitle)}</div>
</body>
</html>`;
}

/**
 * Trigger browser download of the charter as a Word document (.doc).
 */
export function downloadCharterWord(options) {
  const html = buildCharterWordHtml(options);
  const entity = options.charterScope === 'project'
    ? (options.projectName || 'project')
    : (options.programmeName || 'programme');
  const filename = `${slugify(options.charterScope === 'project' ? 'project-charter' : 'programme-charter')}-${slugify(entity)}.doc`;

  const blob = new Blob(['\ufeff', html], { type: 'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);

  return filename;
}
